/**
 * The intent-first sign-up as a path of screens, for the step indicator and the
 * Back button of IntentSignupFlow. Pure functions, so the two always agree and are
 * tested without rendering (__tests__/components/signupPlan.test.ts).
 *
 *   visitor / owner:        intent → account → verify                    3 steps
 *   marketer, individual:   intent → account → verify → pro              4 steps
 *   marketer, company:      intent → company → account → verify → pro    5 steps
 *   developer (company):    intent → company → account → verify → pro    5 steps
 *   deep link (?role=…):    roles → [company] → account → verify → pro   4 or 5 steps
 *
 * Navigation is a history stack: going forward pushes the screen being left, Back
 * pops it. Back therefore only ever returns to a screen the person actually saw
 * (the marketer and developer paths used to jump to `roles`, which they never
 * pass through). With nothing left to pop it falls back to `intent`, which is
 * where Back leads from the deep-linked roles screen.
 */
import type { AccountKind } from './roleCatalog';

export type SignupScreen = 'intent' | 'roles' | 'company' | 'account' | 'verify' | 'pro';

// 'visitor' = a plain browsing/consumer account (replaces the old buy/rent split);
// 'professional' covers marketer/developer (and deep-linked pro roles); 'own' is
// the landlord "I own a property" path. buy/rent are kept in the union only so
// deep links / older state don't break — they behave like 'visitor'.
export type SignupIntent = 'visitor' | 'buy' | 'rent' | 'professional' | 'own';

export interface SignupNav {
  /** Screens visited before the current one, oldest first. */
  history: SignupScreen[];
  /** The screen on show. */
  screen: SignupScreen;
}

export const INITIAL_SIGNUP_NAV: SignupNav = { history: [], screen: 'intent' };

/** Start the path at `screen` with nothing behind it (the `?role=` deep link starts at roles). */
export function startAt(screen: SignupScreen): SignupNav {
  return { history: [], screen };
}

/** Move forward to `next`, remembering the screen being left. */
export function goTo(nav: SignupNav, next: SignupScreen): SignupNav {
  if (next === nav.screen) return nav;
  // Going to a screen that is already behind us rewinds to it, so the path never
  // lists a screen twice.
  const seen = nav.history.indexOf(next);
  if (seen >= 0) return { history: nav.history.slice(0, seen), screen: next };
  return { history: [...nav.history, nav.screen], screen: next };
}

/** Return to the screen before this one; with none left, to `intent`. */
export function goBack(nav: SignupNav): SignupNav {
  const { history } = nav;
  if (history.length === 0) return nav.screen === 'intent' ? nav : INITIAL_SIGNUP_NAV;
  return { history: history.slice(0, -1), screen: history[history.length - 1] };
}

/** The screens still ahead of `screen` on the path the current choices lead to. */
export function screensAfter(
  screen: SignupScreen,
  intent: SignupIntent | null,
  accountKind: AccountKind,
): SignupScreen[] {
  // Roles, company details and pro onboarding only exist on the professional path.
  const professional = intent === 'professional' || screen === 'roles' || screen === 'company' || screen === 'pro';
  const fromAccount: SignupScreen[] = professional ? ['account', 'verify', 'pro'] : ['account', 'verify'];
  switch (screen) {
    case 'intent':
    case 'roles':
      // The intent screen's marketer and developer choices skip roles, so roles
      // is never ahead of intent; company details come first for a company.
      return professional && accountKind === 'company' ? ['company', ...fromAccount] : fromAccount;
    case 'company':
      return fromAccount;
    case 'account':
      return fromAccount.slice(1);
    case 'verify':
      return professional ? ['pro'] : [];
    case 'pro':
      return [];
  }
}

export interface SignupPlan {
  /** Every screen of the path in order: the visited ones, the current one, then the rest. */
  steps: SignupScreen[];
  /** Index of the current screen in `steps`, i.e. how many screens came before it. */
  current: number;
}

/**
 * The whole path as the person sees it now. Before a choice is made on the intent
 * screen, the shortest path (visitor, 3 steps) is shown; it grows as soon as a
 * longer path is picked.
 */
export function planFor(
  history: readonly SignupScreen[],
  screen: SignupScreen,
  intent: SignupIntent | null,
  accountKind: AccountKind,
): SignupPlan {
  return {
    steps: [...history, screen, ...screensAfter(screen, intent, accountKind)],
    current: history.length,
  };
}
